import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import { CLIENT_CONTEXT_TOKEN_PCS } from '@/api/pcs-api/tokens';

import { extractProblemDetailsBody, isRecord } from './problem-details.util';

/**
 * Preserves RFC 9457 ProblemDetails bodies for PCS requests (DESIGN-004 R-a1,
 * routed from the TASK-180 review gate).
 *
 * The global `ErrorInterceptor` (`@trueform/ui/core`) rewrites every
 * `HttpErrorResponse` into a DCS-style `ErrorPayload`, synthesizing
 * `{ code: 'HTTP_ERROR', message, message_key }` whenever the body is not the
 * DCS `{ error: { code, … } }` envelope — so PCS's TOP-LEVEL ProblemDetails
 * body (F8: `title`, `detail`, `code`, `correlation_id`, `error_id` on 5xx)
 * is destroyed before any service subscriber sees it, and U5's
 * `toCalcFeedback` degrades to the fallback message. A subscriber-side
 * `catchError` cannot recover the body (it runs downstream of the
 * interceptor chain), so the body must be preserved INSIDE the chain.
 *
 * Errors propagate innermost-first, so this interceptor MUST be registered
 * AFTER `ErrorInterceptor` in `HTTP_INTERCEPTORS` (the `app.config.ts`
 * position next to `PhsProblemDetailsInterceptor`, whose shape this clones).
 * It rethrows the ProblemDetails BODY as a plain object: the outer
 * `ErrorInterceptor`'s `instanceof HttpErrorResponse` check then fails and
 * passes it through untouched (no toast), while U5 reads the body directly
 * (`detail ?? title`, `code`, `correlation_id`, `error_id`).
 *
 * The defusing scope is deliberately narrow:
 * - only requests marked as PCS (`CLIENT_CONTEXT_TOKEN_PCS`, the same context
 *   `PcsTariffHttpService` and the generated PCS services attach) — every
 *   other client (DCS, PHS, auth) keeps its exact current error flow;
 * - only non-401 failures — 401 keeps the global logout/redirect flow;
 * - only ProblemDetails-shaped bodies (a record with a non-blank string
 *   `title`). DCS envelopes, HTML 5xx pages and empty bodies pass through
 *   untouched so the library's own handling keeps working.
 * - a `blob` error body (the calculation export uses `responseType: 'blob'`)
 *   is decoded as text and, when it is Problem Details, rethrown as that
 *   object. The bytes are never left for a caller to save as a document.
 *
 * Lives in `core/interceptors/` as a PERMANENT, application-wide component.
 */
@Injectable()
export class PcsProblemDetailsInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.context.has(CLIENT_CONTEXT_TOKEN_PCS)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(catchError((err: unknown) => this.rethrowPreserved(err)));
  }

  /**
   * JSON errors stay synchronous. Blob errors (export) are read as text first
   * so a Problem Details body can be rethrown before `ErrorInterceptor` turns
   * the `HttpErrorResponse` into `HTTP_ERROR`. 401 is never decoded.
   */
  private rethrowPreserved(err: unknown): Observable<never> {
    if (err instanceof HttpErrorResponse && err.status !== 401 && err.error instanceof Blob) {
      return from(err.error.text()).pipe(
        catchError(() => throwError(() => err)),
        switchMap((text) => {
          const parsed = parseJsonRecord(text);
          if (parsed === null) {
            return throwError(() => err);
          }
          const synthetic = new HttpErrorResponse({
            error: parsed,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined,
          });
          return throwError(() => extractProblemDetailsBody(synthetic) ?? err);
        }),
      );
    }
    return throwError(() => extractProblemDetailsBody(err) ?? err);
  }
}

function parseJsonRecord(text: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
